import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Get initial theme from DOM (set by inline script in index.html)
function getInitialTheme(): Theme {
  if (typeof window !== "undefined") {
    // Check if dark class is already applied by inline script
    const isDarkMode = document.documentElement.classList.contains("dark");
    if (isDarkMode) return "dark";
    
    // Fallback to localStorage
    const stored = localStorage.getItem("theme") as Theme;
    return stored || "light";
  }
  return "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initialize from DOM to avoid mismatch with inline script
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    
    // Batch DOM updates to prevent flashing
    if (theme === "dark") {
      if (!root.classList.contains("dark")) {
        root.classList.add("dark");
      }
    } else {
      if (root.classList.contains("dark")) {
        root.classList.remove("dark");
      }
    }
    
    // Save to localStorage
    localStorage.setItem("theme", theme);
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
